import React, { useCallback, useEffect, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import LandingButton from "./LandingButton"
import { defaultHeroSlides, type HeroSlide } from "./landingHeroSlides"
import apiClient from "../../lib/api"
import { resolveImageUrl } from "../../lib/imageUtils"

const SLIDE_DURATION_MS = 6500
const RIPPLE_INTERVAL_MS = 90
const MAX_RIPPLES = 12

type Ripple = {
  id: number
  x: number
  y: number
}

const contentVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.15 },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
}

const LandingHero: React.FC = () => {
  const prefersReducedMotion = useReducedMotion()
  const containerRef = useRef<HTMLElement>(null)
  const rippleId = useRef(0)
  const lastRippleTime = useRef(0)
  const [slides, setSlides] = useState<HeroSlide[]>(defaultHeroSlides)
  const [activeIndex, setActiveIndex] = useState(0)
  const [progress, setProgress] = useState(0)
  const [ripples, setRipples] = useState<Ripple[]>([])

  useEffect(() => {
    apiClient.getHeroSlides().then((data) => {
      if (data.length === 0) return
      setSlides(
        [...data]
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .map((slide, index) => ({
            id: String(index),
            src: resolveImageUrl(slide.src),
            label: slide.label,
            caption: slide.caption,
          }))
      )
    })
  }, [])

  useEffect(() => {
    if (activeIndex >= slides.length) {
      setActiveIndex(0)
      setProgress(0)
    }
  }, [activeIndex, slides.length])

  const removeRipple = useCallback((id: number) => {
    setRipples((current) => current.filter((ripple) => ripple.id !== id))
  }, [])

  const spawnRipple = useCallback(
    (clientX: number, clientY: number) => {
      if (prefersReducedMotion || !containerRef.current) return

      const now = performance.now()
      if (now - lastRippleTime.current < RIPPLE_INTERVAL_MS) return
      lastRippleTime.current = now

      const rect = containerRef.current.getBoundingClientRect()
      const id = rippleId.current++
      setRipples((current) => [
        ...current.slice(-(MAX_RIPPLES - 1)),
        { id, x: clientX - rect.left, y: clientY - rect.top },
      ])
    },
    [prefersReducedMotion]
  )

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      spawnRipple(event.clientX, event.clientY)
    },
    [spawnRipple]
  )

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (prefersReducedMotion || !containerRef.current) return

      const rect = containerRef.current.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top

      // Stronger double ripple on click
      const baseId = rippleId.current
      rippleId.current += 2
      setRipples((current) => [
        ...current.slice(-(MAX_RIPPLES - 2)),
        { id: baseId, x, y },
        { id: baseId + 1, x, y },
      ])
    },
    [prefersReducedMotion]
  )

  useEffect(() => {
    if (prefersReducedMotion || slides.length === 0) return

    setProgress(0)
    const startedAt = performance.now()
    let frame = 0

    const tick = (now: number) => {
      const elapsed = now - startedAt
      const nextProgress = Math.min(elapsed / SLIDE_DURATION_MS, 1)
      setProgress(nextProgress)

      if (elapsed >= SLIDE_DURATION_MS) {
        setActiveIndex((current) => (current + 1) % slides.length)
        return
      }

      frame = requestAnimationFrame(tick)
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [activeIndex, prefersReducedMotion, slides.length])

  const activeSlide = slides[activeIndex] ?? slides[0]
  if (!activeSlide) return null

  return (
    <section
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
      className="relative flex min-h-screen flex-col overflow-hidden bg-[#0a0a12]"
    >
      {/* Keep every slide image mounted so Pexels is only fetched once per URL */}
      <div className="absolute inset-0">
        {slides.map((slide, index) => {
          const isActive = index === activeIndex
          return (
            <motion.div
              key={slide.id}
              className="absolute inset-0"
              initial={false}
              animate={{
                opacity: isActive ? 1 : 0,
                // Inactive starts zoomed so the active transition Ken-Burns in
                scale: prefersReducedMotion ? 1 : isActive ? 1 : 1.12,
              }}
              transition={{
                duration: prefersReducedMotion ? 0.3 : 1.6,
                ease: [0.22, 1, 0.36, 1],
              }}
              style={{ zIndex: isActive ? 1 : 0 }}
            >
              <img
                src={slide.src}
                alt=""
                className="h-full w-full object-cover"
                loading={index === 0 ? "eager" : "lazy"}
                decoding="async"
                aria-hidden
              />
            </motion.div>
          )
        })}
      </div>

      {/* Gradient overlays */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#001a4d]/85 via-[#0d0d0d]/55 to-[#2d1a45]/75" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_20%_0%,rgba(158,126,67,0.22),transparent_45%)]" />

      {/* Water ripples */}
      {!prefersReducedMotion && (
        <div className="pointer-events-none absolute inset-0 z-[6] overflow-hidden">
          <AnimatePresence>
            {ripples.map((ripple, index) => (
              <motion.span
                key={ripple.id}
                className="absolute rounded-full blur-md"
                style={{ left: ripple.x, top: ripple.y }}
                initial={{ width: 0, height: 0, x: "-50%", y: "-50%", opacity: 0.4, scale: 0.7 }}
                animate={{
                  width: index % 2 === 0 ? 320 : 460,
                  height: index % 2 === 0 ? 320 : 460,
                  opacity: [0.4, 0.22, 0],
                  scale: [0.7, 1, 1.05],
                }}
                exit={{ opacity: 0 }}
                transition={{ duration: 3, ease: [0.16, 1, 0.3, 1], times: [0, 0.5, 1] }}
                onAnimationComplete={() => removeRipple(ripple.id)}
              >
                <span className="absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(214,229,255,0.32)_0%,rgba(158,126,67,0.1)_38%,transparent_68%)]" />
                <span className="absolute inset-[18%] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,0.14)_0%,transparent_72%)] blur-md" />
              </motion.span>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Floating gradient orbs */}
      {!prefersReducedMotion && (
        <>
          <motion.div
            className="pointer-events-none absolute -left-24 top-1/4 h-80 w-80 rounded-full bg-dc-primary-900/25 blur-[100px]"
            animate={{ x: [0, 50, 0], y: [0, -35, 0] }}
            transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="pointer-events-none absolute -right-16 bottom-1/4 h-72 w-72 rounded-full bg-[#9e7e43]/20 blur-[90px]"
            animate={{ x: [0, -40, 0], y: [0, 30, 0] }}
            transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
          />
        </>
      )}

      {/* Film grain */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.035] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
        aria-hidden
      />

      <div className="relative z-10 flex flex-1 flex-col px-4 pb-28 pt-24 md:px-8 lg:px-20">
        <motion.div
          className="mx-auto flex flex-1 max-w-4xl flex-col items-center justify-center gap-6 px-4 text-center md:gap-8"
          variants={contentVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 font-dm-sans text-xs font-semibold uppercase tracking-[0.2em] text-white/90 backdrop-blur-md">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#9e7e43] opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#9e7e43]" />
              </span>
              Welcome to Dominion City Ireland
            </span>
          </motion.div>

          <motion.h1
            variants={itemVariants}
            className="max-w-3xl font-outfit text-4xl font-semibold leading-[1.08] tracking-tight text-white md:text-5xl lg:text-[4.25rem]"
          >
            Experience God,{" "}
            <span className="bg-gradient-to-r from-[#D6E5FF] via-white to-[#9e7e43] bg-clip-text text-transparent">
              Find Family
            </span>{" "}
            & Discover Purpose.
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="max-w-xl font-dm-sans text-base font-medium leading-7 tracking-wide text-white/80 md:text-lg"
          >
            Join us this Sunday and discover a place where faith becomes real, community feels like
            family, and your purpose comes alive in Christ.
          </motion.p>

          <motion.div variants={itemVariants} className="flex w-full max-w-xl justify-center">
            <LandingButton href="/#our-locations" className="min-w-[180px]">
              Plan a Visit
            </LandingButton>
          </motion.div>
        </motion.div>
      </div>

      {/* Slide controls + caption */}
      <div className="absolute bottom-8 left-0 right-0 z-20 px-4 md:px-8 lg:px-20">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSlide.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.45 }}
              className="flex flex-col gap-1"
            >
              <span className="font-dm-sans text-xs font-semibold uppercase tracking-[0.22em] text-[#9e7e43]">
                {activeSlide.label}
              </span>
              <span className="font-outfit text-lg text-white/90 md:text-xl">{activeSlide.caption}</span>
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center gap-2.5" role="tablist" aria-label="Hero slideshow">
            {slides.map((slide, index) => {
              const fillScale =
                index < activeIndex ? 1 : index === activeIndex ? progress : 0

              return (
                <button
                  key={slide.id}
                  type="button"
                  role="tab"
                  aria-selected={index === activeIndex}
                  aria-label={`Show ${slide.label}`}
                  onClick={(event) => {
                    event.stopPropagation()
                    setActiveIndex(index)
                  }}
                  className="relative h-1.5 w-10 overflow-hidden rounded-full bg-white/25 sm:w-12"
                >
                  <span
                    className="absolute inset-0 origin-left rounded-full bg-gradient-to-r from-dc-primary-900 to-[#9e7e43]"
                    style={{ transform: `scaleX(${fillScale})` }}
                  />
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

export default LandingHero
